// CONTRACT STUB (owner: lane F1). Labels only; the server enum stays LensPlanMode.
import React from 'react';

import { LENS_PLAN_MODES, type LensPlanMode } from '../../../types/leLens';

export type LensShape = {
  mode: LensPlanMode;
  label: string;
  description: string;
  examples: ReadonlyArray<string>;
};

/**
 * Board-shape starting points, keyed by the server's own `LensPlanMode` enum so a redesign of the
 * copy never touches the wire value. Labels are the plain taxonomy nouns Ming asked to keep
 * (live feedback on the first pass, which tried question-form labels instead): Track / Explain /
 * Explore, matching `monitor` / `diagnose` / `explore` one-to-one. Picking one only carries a
 * hint: `runPlanStep` can still land on a different mode from the brief itself. Descriptions are
 * kept short enough to show in full on two lines at 1440px with no truncation (Ming's ask).
 */
export const LENS_SHAPES: ReadonlyArray<LensShape> = [
  {
    description: 'Numbers to watch against a comparison.',
    examples: [
      'Track TTV for Australia this month versus last month',
      'Show margin by sale channel for the last 30 days',
    ],
    label: 'Track',
    mode: 'monitor',
  },
  {
    description: 'What changed and what drove it.',
    examples: [
      'Which offers had lower margin this month than last month',
      'Why did TTV in Australia fall last week versus the week before',
      'What drove the change in paid search bookings this month versus last month',
    ],
    label: 'Explain',
    mode: 'diagnose',
  },
  {
    description: 'A survey of one topic and its useful cuts.',
    examples: ['Explore bookings by destination and customer type over the last 90 days'],
    label: 'Explore',
    mode: 'explore',
  },
];

// Every server mode has exactly one shape, and every shape names a real server mode: the empty
// state has nothing to show for a mode this array left out.
if (process.env.NODE_ENV !== 'production') {
  const shapeModes = new Set(LENS_SHAPES.map((shape) => shape.mode));
  const missing = LENS_PLAN_MODES.filter((mode) => !shapeModes.has(mode));
  if (missing.length > 0) {
    console.warn(`lensShapes.tsx: LENS_SHAPES is missing a shape for mode(s): ${missing.join(', ')}`);
  }
}

export function lensShapeFor(mode: LensPlanMode | undefined): LensShape | undefined {
  return LENS_SHAPES.find((shape) => shape.mode === mode);
}

/**
 * A generic board silhouette per shape (not data): the canvas's own `t-track` / `t-why` /
 * `t-explore` symbols, inlined. `size` is the rendered width in px; height follows the symbol's
 * own 240x120 aspect ratio.
 */
export function LensShapeThumbnail({ mode, size = 240 }: { mode: LensPlanMode; size?: number }): React.ReactElement {
  const height = size / 2;
  switch (mode) {
    case 'monitor':
      return (
        <svg aria-hidden focusable="false" height={height} viewBox="0 0 240 120" width={size}>
          <rect fill="#fff" height="34" rx="5" stroke="rgba(15,70,79,.25)" width="115" x="1" y="1" />
          <rect fill="#fff" height="34" rx="5" stroke="rgba(15,70,79,.25)" width="115" x="124" y="1" />
          <rect fill="#0F464F" height="5" rx="2.5" width="46" x="12" y="12" />
          <rect fill="rgba(15,70,79,.3)" height="3" rx="1.5" width="28" x="12" y="22" />
          <rect fill="#0F464F" height="5" rx="2.5" width="46" x="135" y="12" />
          <rect fill="rgba(15,70,79,.3)" height="3" rx="1.5" width="28" x="135" y="22" />
          <rect fill="#fff" height="76" rx="5" stroke="rgba(15,70,79,.25)" width="238" x="1" y="43" />
          <polyline
            fill="none"
            points="14,100 50,90 80,94 115,74 150,80 185,62 226,66"
            stroke="#0F464F"
            strokeLinejoin="round"
            strokeWidth="2"
          />
          <polyline
            fill="none"
            points="14,104 50,98 80,99 115,90 150,92 185,84 226,86"
            stroke="rgba(15,70,79,.3)"
            strokeDasharray="3 3"
            strokeWidth="1.5"
          />
        </svg>
      );
    case 'diagnose':
      return (
        <svg aria-hidden focusable="false" height={height} viewBox="0 0 240 120" width={size}>
          <rect fill="#fff" height="34" rx="5" stroke="rgba(15,70,79,.25)" width="238" x="1" y="1" />
          <rect fill="#0F464F" height="5" rx="2.5" width="60" x="12" y="12" />
          <rect fill="rgba(15,70,79,.3)" height="3" rx="1.5" width="110" x="12" y="22" />
          <rect fill="#fff" height="76" rx="5" stroke="rgba(15,70,79,.25)" width="238" x="1" y="43" />
          <rect fill="#0F464F" height="48" width="26" x="16" y="58" />
          <rect fill="rgba(15,70,79,.35)" height="14" width="26" x="50" y="58" />
          <rect fill="rgba(15,70,79,.35)" height="10" width="26" x="84" y="72" />
          <rect fill="#4EFEB9" height="8" width="26" x="118" y="82" />
          <rect fill="rgba(15,70,79,.35)" height="6" width="26" x="152" y="76" />
          <rect fill="#0F464F" height="30" width="36" x="186" y="76" />
        </svg>
      );
    case 'explore':
    default:
      return (
        <svg aria-hidden focusable="false" height={height} viewBox="0 0 240 120" width={size}>
          <g fill="#fff" stroke="rgba(15,70,79,.25)">
            <rect height="56" rx="5" width="75" x="1" y="1" />
            <rect height="56" rx="5" width="76" x="82" y="1" />
            <rect height="56" rx="5" width="75" x="164" y="1" />
            <rect height="56" rx="5" width="75" x="1" y="63" />
            <rect height="56" rx="5" width="76" x="82" y="63" />
            <rect height="56" rx="5" width="75" x="164" y="63" />
          </g>
          <g fill="#0F464F">
            <rect height="18" width="10" x="12" y="30" />
            <rect height="26" width="10" x="26" y="22" />
            <rect height="12" width="10" x="40" y="36" />
            <rect height="5" rx="2" width="40" x="94" y="14" />
            <rect height="5" rx="2" width="28" x="94" y="24" />
            <rect height="5" rx="2" width="52" x="94" y="34" />
            <rect height="4" rx="2" width="54" x="12" y="76" />
            <rect height="4" rx="2" width="40" x="12" y="86" />
            <rect height="4" rx="2" width="48" x="12" y="96" />
          </g>
          <polyline fill="none" points="174,44 190,34 204,38 228,18" stroke="#0F464F" strokeWidth="2" />
          <circle cx="120" cy="92" fill="none" r="16" stroke="#0F464F" strokeDasharray="60 41" strokeWidth="7" />
          <polyline fill="none" points="174,106 190,96 204,100 228,82" stroke="rgba(15,70,79,.35)" strokeWidth="2" />
        </svg>
      );
  }
}
